import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUsageResetCountdown } from '../../utils/usageReset';
import './FleetUsageTags.css';

export default function FleetUsageTags({ node }) {
  const { t, i18n } = useTranslation();
  const key = 'aiFleet.usageTags';
  const limits = node.usageLimits || [];
  const recoveryMinutes = Math.ceil(Number(node.clears_in) / 60);
  const [now, setNow] = useState(Date.now);
  const [activeKind, setActiveKind] = useState(null);
  const rootRef = useRef(null);
  const tooltipId = useId();
  const activeLimit = limits.find(limit => limit.kind === activeKind);
  const labelFor = limit => t(`${key}.${limit.kind === 'session' ? 'session' : 'weekly'}`);
  const reveal = kind => {
    setNow(Date.now());
    setActiveKind(kind);
  };

  useEffect(() => {
    if (!activeKind) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60000);
    const dismissOutside = event => {
      if (!rootRef.current?.contains(event.target)) setActiveKind(null);
    };
    const dismissOnEscape = event => {
      if (event.key === 'Escape') setActiveKind(null);
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      clearInterval(timer);
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [activeKind]);

  const reset = activeLimit ? getUsageResetCountdown(activeLimit.resetsAt, now) : null;

  return (
    <div className="fleet-usage" ref={rootRef} role="group" aria-label={t(`${key}.label`)}
      onMouseLeave={() => { if (!rootRef.current?.contains(document.activeElement)) setActiveKind(null); }}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setActiveKind(null); }}>
      <div className="fleet-usage-grid">
        {limits.map(limit => {
          const color = limit.percent >= 100 || limit.severity === 'critical' ? 'is-critical'
            : limit.severity === 'warning' ? 'is-warning'
            : !limit.severity && limit.percent >= 90 ? 'is-critical'
            : !limit.severity && limit.percent >= 75 ? 'is-warning' : 'is-normal';
          const label = labelFor(limit);
          const used = t(`${key}.used`, {
            percent: new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 1 }).format(limit.percent),
          });
          return (
            <button type="button" key={limit.kind} className="fleet-usage-tile"
              aria-label={t(`${key}.viewReset`, { period: label, usage: used })}
              aria-describedby={activeKind === limit.kind ? tooltipId : undefined}
              onMouseEnter={() => reveal(limit.kind)} onFocus={() => reveal(limit.kind)}
              onClick={() => reveal(limit.kind)}>
              <span className="fleet-usage-period">{label}{node.usageStale && <span className="fleet-usage-stale-dot" aria-label={t(`${key}.stale`)} />}</span>
              <span className={`fleet-usage-value ${color}`}>{used}</span>
            </button>
          );
        })}
        {limits.length === 0 && <div className="fleet-usage-empty">{t(`${key}.unavailable`)}</div>}
      </div>
      {activeLimit && <div id={tooltipId} role="tooltip" className="fleet-usage-tooltip">
        <span className="fleet-usage-tooltip-title">{labelFor(activeLimit)} · {t(`${key}.resetTitle`)}</span>
        <span>{t(`${key}.${reset.key}`, reset)}</span>
        {node.usageStale && <span className="fleet-usage-tooltip-note">{t(`${key}.stale`)}</span>}
        {node.status === 'maintenance' && Number.isFinite(recoveryMinutes) && recoveryMinutes > 0 && (
          <span className="fleet-usage-tooltip-note">
            {t(`${key}.recovery`, { hours: Math.floor(recoveryMinutes / 60), minutes: recoveryMinutes % 60 })}
          </span>
        )}
      </div>}
    </div>
  );
}
